// Die Steckdosen (Z-Wave und Zigbee-Energie) -- EIN gemeinsamer Stand für
// zwave-summary und energy-summary.
//
// Datenfluss (seit 02.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Steckdosen über das Protokoll“): jedes Z-Wave- und jedes Zigbee-
// Energiegerät ist eine Instanz steckdose.<id>.stand. Das Backend
// (internal/protokoll/steckdosen) gleicht die Caches der beiden MQTT-Handler
// auf Anstoß (jede Meldung eines Geräts, gebündelt 250 ms) und alle 60 s ab;
// der Hub verwirft Unverändertes und schickt Deltas. Bis dahin holte jeder der
// beiden Stores seine Liste alle 5 s (Energie) bzw. 30 s (Z-Wave) per REST und
// zusätzlich bei jedem WS-Signal -- je Tab, je Store.
//   - Hier steht die einzige Abo-Stelle (`steckdose.*.stand`). Aus einem Stand
//     baut lib/steckdosen-protokoll.ts beide REST-Formen: die Z-Wave-Liste
//     (`GET /v2-api/zwave/devices`) und die Energie-Liste
//     (`GET /v2-api/energy/devices`, Z-Wave-Geräte nur mit Leistung oder
//     Zählerstand).
//   - REST (beide Endpunkte) nur beim Start, als Revalidator (Reconnect, Tab
//     kommt zurück), in refreshSteckdosen (nach einer Aktion) und als
//     Neuversuch alle 15 s, solange ein Abruf scheiterte. KEIN Takt, KEIN Abo
//     auf die alten WS-Kanäle "energy" und "zwave" mehr.
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein.
//
// Mehrere Stores teilen sich den Stand: startSteckdosen() zählt mit und
// liefert die Abmeldung; der letzte, der geht, schließt ab.
//
// SWR: ja -- die zuletzt gesehenen Listen kommen beim Start aus der Ablage.

import { getJSON } from "./api";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { SteckdoseStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import {
  energieGeraetAusProtokoll,
  energieSchluessel,
  steckdoseIdAusInstanz,
  vergleicheEnergie,
  vergleicheZwave,
  zwaveGeraetAusProtokoll,
} from "./steckdosen-protokoll";
import type { EnergyDevice, ZwaveDevice } from "./types";

const STORAGE_KEY = "steckdosen";

interface Gespeichert {
  zwave: ZwaveDevice[];
  energie: EnergyDevice[];
}

const _zwave = new ProtokollListe<ZwaveDevice>((e) => e.name, vergleicheZwave);
const _energie = new ProtokollListe<EnergyDevice>((e) => energieSchluessel(e.source, e.device), vergleicheEnergie);
// Ob die Listen etwas wissen (Ablage, Abruf oder Stand): vorher `undefined`.
let _geladen = $state(false);
let _nutzer = 0;
let _unsub: Array<() => void> = [];
// Instanzname -> Quelle und Name: `weg` nennt nur die Instanz.
const _instanzen = new Map<string, { quelle: string; name: string }>();

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (Leistungswerte ändern sich im Sekunden-
// takt): gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const g: Gespeichert = { zwave: $state.snapshot(_zwave.items), energie: $state.snapshot(_energie.items) };
    persist(STORAGE_KEY, g);
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  if (steckdoseIdAusInstanz(instanz) === undefined) return;
  const st = skaliere<SteckdoseStand>("steckdose.stand", daten);
  const quelle = st.quelle ?? "";
  const name = st.name ?? "";
  if (quelle !== "zwave" && quelle !== "zigbee") return;
  _instanzen.set(instanz, { quelle, name });
  let geaendert = false;
  if (quelle === "zwave") geaendert = _zwave.stand(zwaveGeraetAusProtokoll(st)) || geaendert;
  const en = energieGeraetAusProtokoll(st);
  geaendert = (en ? _energie.stand(en) : _energie.weg(energieSchluessel(quelle, name))) || geaendert;
  // Vor dem ersten Abruf legt laden() die Geräte über die Antwort; bis dahin
  // wäre die Liste leer, obwohl Stände da sind.
  if (_zwave.bereit) _geladen = true;
  if (geaendert) persistSpaeter();
}

function beiWeg(instanz: string): void {
  const i = _instanzen.get(instanz);
  if (!i) return;
  _instanzen.delete(instanz);
  let geaendert = false;
  if (i.quelle === "zwave") geaendert = _zwave.weg(i.name) || geaendert;
  geaendert = _energie.weg(energieSchluessel(i.quelle, i.name)) || geaendert;
  if (geaendert) persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholtes,
// inzwischen verschwundenes Gerät stehen (für das kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const abZ = _zwave.nr;
  const abE = _energie.nr;
  const lauf = _lauf;
  // Die beiden Listen getrennt: scheitert eine, gilt die andere trotzdem.
  const [z, e] = await Promise.allSettled([
    getJSON<ZwaveDevice[]>("/v2-api/zwave/devices"),
    getJSON<EnergyDevice[]>("/v2-api/energy/devices"),
  ]);
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem -- beim ersten Abruf wären die Listen sonst leer, bis jedes
    // Gerät sich ändert.
    const gz = _zwave.mische(z.status === "fulfilled" ? z.value : undefined, abZ);
    const ge = _energie.mische(e.status === "fulfilled" ? e.value : undefined, abE);
    if (gz || ge) persistSpaeter();
    if (z.status === "fulfilled" || e.status === "fulfilled" || _zwave.neuer(abZ) || _energie.neuer(abE)) _geladen = true;
  } finally {
    _zwave.bereit = true;
    _energie.bereit = true;
  }
  if ((z.status === "rejected" || e.status === "rejected") && _nutzer > 0 && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_nutzer > 0) void laden();
    }, NEUVERSUCH_MS);
  }
}

/** Meldet einen Nutzer des gemeinsamen Stands an (der erste startet Abo und
 *  Abruf) und liefert die Abmeldung (der letzte schließt ab). */
export function startSteckdosen(): () => void {
  _nutzer++;
  if (_nutzer === 1) {
    _lauf++;
    // SWR: gecachte Listen sofort einspielen. Das Protokoll bringt die frischen
    // Werte danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
    const cached = loadPersisted<Gespeichert>(STORAGE_KEY);
    if (cached) {
      _zwave.items = cached.zwave ?? [];
      _energie.items = cached.energie ?? [];
      _geladen = true;
    }
    void laden();
    _unsub.push(abonniere("steckdose.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }));
    // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
    // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
    // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
    _unsub.push(registerRevalidator(laden));
  }
  let aktiv = true;
  return () => {
    if (!aktiv) return;
    aktiv = false;
    _nutzer--;
    if (_nutzer === 0) stoppe();
  };
}

function stoppe(): void {
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _zwave.leere();
  _energie.leere();
  _instanzen.clear();
  _geladen = false;
}

/** Die Z-Wave-Geräte (wie `GET /v2-api/zwave/devices`); `undefined`, solange
 *  weder Ablage noch Abruf noch Protokoll etwas geliefert haben. */
export function getZwaveGeraete(): ZwaveDevice[] | undefined {
  return _geladen ? _zwave.items : undefined;
}

/** Die Energie-Geräte (wie `GET /v2-api/energy/devices`); `undefined`, solange
 *  nichts bekannt ist. */
export function getEnergieGeraete(): EnergyDevice[] | undefined {
  return _geladen ? _energie.items : undefined;
}

/** Holt beide Listen sofort per REST -- nach einer Aktion, deren Ergebnis die
 *  Seite gleich zeigen soll (das Protokoll braucht bis zu 250 ms). */
export function refreshSteckdosen(): Promise<void> {
  // Ohne Nutzer (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (_nutzer === 0) return Promise.resolve();
  return laden();
}
